import React, { useState } from 'react';
import ArrowLeft from 'ui/ds/icons/TelehealthIcons/ArrowLeft';
import {
  AngryIcon,
  HappyIcon,
  NeutralIcon,
  OkayIcon,
  SadIcon,
} from 'ui/ds/icons/TelehealthIcons/FeedbackIcons';
import { redirectToPlumApp, track } from '@/utils';
import useTelehealth from '@/hooks/useTelehealth';
import { useMutation } from '@tanstack/react-query';
import { submitFeedbackQuery } from '@/queries/telehealth.queries';
import { Spinner } from 'ui/mobile-ds';
import toast from 'react-hot-toast';
import { CheckCircle, CloseFilled } from 'ui/icons';
import { TRACKER_EVENTS } from '@/constants';
import { get } from 'lodash';
import { twConfig } from 'ui/ds/utils/getConfig';
import { ChevronLeft } from 'ui/retail-ds/icons';
import useBreakpoints from '@/hooks/useBreakpoints';

const CallEndedFeedback: React.FC = () => {
  const { isDesktop } = useBreakpoints();

  const { appointmentId, necessaryAppointmentDetails } = useTelehealth();

  const { doctorInfo } = necessaryAppointmentDetails;

  const [consultationRating, setConsultationRating] = useState<number | null>(
    null,
  );
  const [callExperienceRating, setCallExperienceRating] = useState<
    number | null
  >(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [loading, setLoading] = useState(false);
  const feedbackMutation = useMutation({
    mutationFn: submitFeedbackQuery,
    onSuccess: () => {
      /**
       * On success, send a feedback submitted event
       */
      track({
        event: TRACKER_EVENTS.TELHEALTH_FEEDBACK_SUBMITTED,
        data: {
          source: 'web',
          specialty:
            necessaryAppointmentDetails.doctorInfo?.specialties?.join(' '),
        },
      });
      toast.custom((t) => (
        <div className="w-[440px] py-5 px-3 bg-gray-600 rounded-xl shadow-lg flex items-center gap-2 justify-center">
          <div>
            <CheckCircle />
          </div>
          {consultationRating &&
          consultationRating >= 4 &&
          callExperienceRating &&
          callExperienceRating >= 4 ? (
            <p className="text-white">Thank you for submitting your feedback</p>
          ) : (
            <div className="flex flex-col items-center gap-2 justify-center text-white">
              <p className="text-lg font-semibold">We're sorry about that</p>
              <p style={{ color: get(twConfig, 'theme.colors.mobile.text.hierarchy.2') }}>
                We're working on your feedback immediately
              </p>
            </div>
          )}
          <button
            className="self-end ml-auto"
            onClick={() => toast.dismiss(t.id)}
          >
            <CloseFilled />
          </button>
        </div>
      ));
      /**
       * Added a delay so that the toast doesn't disappear quickly
       */
      setTimeout(() => {
        setLoading(false);
        redirectToPlumApp('/benefits-revamp')();
      }, 2000);
    },
  });

  const { status } = feedbackMutation;

  const feedbackIcons = [AngryIcon, SadIcon, NeutralIcon, OkayIcon, HappyIcon];

  const handleBackToHomescreen = () => {
    redirectToPlumApp('/benefits-revamp')();
  };

  const handleFeedbackSubmit = () => {
    if (!consultationRating) {
      toast.error('Kindly rate your consultation experience!', {
        style: {
          maxWidth: '500px',
          width: 'fit-content',
        },
      });
      return;
    }
    if (!callExperienceRating) {
      toast.error('Kindly rate your video call experience!', {
        style: {
          maxWidth: '500px',
          width: 'fit-content',
        },
      });
      return;
    }
    setLoading(true);
    feedbackMutation.mutate({
      appointmentId: appointmentId ?? '',
      videoExpRating: callExperienceRating ?? 0,
      comment: feedbackText,
      consultantRating: consultationRating ?? 0,
    });
  };

  const doctorName = doctorInfo
    ? `${doctorInfo.title} ${doctorInfo.firstName} ${doctorInfo.lastName}`
    : '';

  const handleKeyDown = (
    e: React.KeyboardEvent,
    rating: number,
    setRating: (rating: number) => void,
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setRating(rating);
    }
  };

  return (
    <div className="min-h-screen bg-surface-100 h-full flex flex-col flex-1 items-center justify-start font-body relative">
      <button
        onClick={handleBackToHomescreen}
        className="text-primary-500 hover:text-primary-600 md:ml-40 self-start flex gap-2 items-center justify-center font-semibold ml-4 lg:ml-20 text-sm absolute top-[50px] lg:top-[100px]"
        aria-label="Go back to homescreen"
      >
        {isDesktop ? (
          <>
            <ArrowLeft color={get(twConfig, 'theme.colors.primary[500]')} />
            <p>Go back to homescreen</p>
          </>
        ) : (
          <ChevronLeft height={24} width={24} fill="#000" />
        )}
      </button>
      <div className="mt-[90px]">
        <h1
          className="text-[32px] font-semibold text-gray-600 mb-12 text-center font-chromatica self-center"
          style={{ lineHeight: '38.4px' }}
        >
          Your call has ended
        </h1>
        <div className="w-full max-w-md bg-white shadow-lg p-5 rounded-[20px] border-brand-gray-100 justify-center border">
          <div className="items-center flex-col justify-center flex">
            <div className="mb-8">
              <label
                htmlFor="consultation-rating"
                className="text-gray-700 font-medium text-sm mb-2 text-center block"
              >
                How was your consultation with {doctorName}?
              </label>
              <div
                id="consultation-rating"
                role="radiogroup"
                aria-label="Consultation rating"
                className="flex gap-3 justify-center"
              >
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => setConsultationRating(rating)}
                    onKeyDown={(e) => handleKeyDown(e, rating, setConsultationRating)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transform transition-transform duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    style={{
                      transform:
                        consultationRating === rating ? 'scale(1.1)' : 'none',
                    }}
                    role="radio"
                    aria-checked={consultationRating === rating}
                    aria-label={`Rate consultation ${rating} out of 5`}
                    tabIndex={consultationRating === rating ? 0 : -1}
                  >
                    {feedbackIcons[rating - 1]({
                      active: consultationRating === rating,
                    })}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <label
                htmlFor="call-experience-rating"
                className="text-gray-700 font-medium mb-2 text-sm text-center block"
              >
                How was your video call experience?
              </label>
              <div
                id="call-experience-rating"
                role="radiogroup"
                aria-label="Video call experience rating"
                className="flex gap-3 justify-center"
              >
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => setCallExperienceRating(rating)}
                    onKeyDown={(e) => handleKeyDown(e, rating, setCallExperienceRating)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transform transition-transform duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    style={{
                      transform:
                        callExperienceRating === rating ? 'scale(1.1)' : 'none',
                    }}
                    role="radio"
                    aria-checked={callExperienceRating === rating}
                    aria-label={`Rate video call experience ${rating} out of 5`}
                    tabIndex={callExperienceRating === rating ? 0 : -1}
                  >
                    {feedbackIcons[rating - 1]({
                      active: callExperienceRating === rating,
                    })}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mb-5">
            <label
              htmlFor="feedback-text"
              className="sr-only"
            >
              Additional feedback
            </label>
            <textarea
              id="feedback-text"
              placeholder="Tell us more"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="w-full p-4 border border-brand-gray-100 rounded-xl resize-none placeholder-gray-400 placeholder:text-sm placeholder:font-normal font-body font-medium focus:outline-gray-600"
              rows={4}
              aria-label="Additional feedback"
            />
          </div>

          <button
            onClick={handleFeedbackSubmit}
            className="w-full bg-primary-500 text-white font-medium py-3 rounded-md hover:bg-primary-600 text-sm flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-500"
            disabled={status === 'pending' || loading}
            aria-busy={status === 'pending' || loading}
          >
            {status === 'pending' ? (
              <>
                <Spinner intent={'secondary'} />
                <span className="sr-only">Submitting feedback...</span>
              </>
            ) : (
              'Give Feedback'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallEndedFeedback;
